/**
 * Team page helpers (multi-clinic v2, Phase 1). Pure functions only.
 */
import {
  CLINIC_ROLE_BADGES,
  CLINIC_ROLE_LABELS,
  PERMISSIONS_REGISTRY,
  PERMISSION_GROUPS,
  PER_THERAPIST_PERMISSION_KEYS,
  PERMISSIONS_BY_KEY,
  deriveClinicRole,
} from "../../config/permissionsRegistry";
import { getOverriddenPermissionKeys } from "../../utils/permissions";

export const ROLE_OPTIONS = [
  { key: "manager", label: CLINIC_ROLE_LABELS.manager },
  { key: "therapist", label: CLINIC_ROLE_LABELS.therapist },
  { key: "secretary", label: CLINIC_ROLE_LABELS.secretary },
];

/** Group order shown in the Team UI (registry order, team/settings last). */
export const UI_GROUP_ORDER = [
  "patients",
  "clinical",
  "treatments",
  "calendar",
  "finance",
  "tasks",
  "leads",
  "reports",
  "team",
  "settings",
];

export const groupLabel = (id) => PERMISSION_GROUPS.find((g) => g.id === id)?.label || id;

/** Per-therapist registry entries grouped for rendering. */
export const groupedPermissionEntries = () =>
  UI_GROUP_ORDER.map((groupId) => ({
    id: groupId,
    label: groupLabel(groupId),
    entries: PERMISSIONS_REGISTRY.filter((e) => !e.clinicMaster && e.group === groupId),
  })).filter((g) => g.entries.length > 0);

export const memberName = (t) => `${t?.firstName || ""} ${t?.lastName || ""}`.trim() || "ללא שם";

export const memberInitials = (t) => {
  const f = (t?.firstName || "").trim()[0] || "";
  const l = (t?.lastName || "").trim()[0] || "";
  return (f + l) || "?";
};

export const roleBadge = (t) => CLINIC_ROLE_BADGES[deriveClinicRole(t)] || CLINIC_ROLE_BADGES.therapist;

/**
 * Keys the admin explicitly overrode for this member — read from
 * `therapist.permissionsOverridden` (legacy docs ⇒ []). Drives the
 * "הרשאות מותאמות" chip on the team cards.
 */
export const overriddenKeys = (therapist, clinic) => {
  const role = deriveClinicRole(therapist);
  if (role === "owner" || role === "manager") return [];
  return getOverriddenPermissionKeys(therapist, clinic);
};

/**
 * Diff two permission maps for the role-change confirm: returns labels of
 * keys that will close (true → false) and open (false → true).
 */
export const presetDiff = (fromMap, toMap) => {
  const closes = [];
  const opens = [];
  PER_THERAPIST_PERMISSION_KEYS.forEach((k) => {
    const a = fromMap?.[k] === true;
    const b = toMap?.[k] === true;
    if (a && !b) closes.push(PERMISSIONS_BY_KEY[k].label);
    if (!a && b) opens.push(PERMISSIONS_BY_KEY[k].label);
  });
  return { closes, opens };
};

/** Hebrew "last login" relative text. */
export const lastLoginText = (t) => {
  const raw = t?.lastLoginDate;
  if (!raw) return "טרם נכנס/ה";
  const d = raw?.toDate ? raw.toDate() : new Date(raw);
  if (Number.isNaN(d.getTime())) return "טרם נכנס/ה";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "נכנס/ה היום";
  if (days === 1) return "נכנס/ה אתמול";
  if (days < 30) return `נכנס/ה לפני ${days} ימים`;
  return `כניסה אחרונה ${d.toLocaleDateString("he-IL")}`;
};

/** Sanitize user-typed add-member form. Returns { field: hebrewError }. */
export const validateMemberForm = (f) => {
  const errors = {};
  if (!f.firstName?.trim()) errors.firstName = "שם פרטי הוא שדה חובה";
  if (!f.lastName?.trim()) errors.lastName = "שם משפחה הוא שדה חובה";
  if (!f.email?.trim()) errors.email = "אימייל הוא שדה חובה";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) errors.email = "כתובת אימייל לא תקינה";
  if (!f.phone || !f.phone.startsWith("+972") || f.phone.length !== 13) {
    errors.phone = "יש להזין מספר טלפון ישראלי תקין";
  }
  return errors;
};
